import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Slider, Tab, Tabs } from "@heroui/react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Key } from "@react-types/shared";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import ActivePlots from "@/components/field/ActivePlots";
import AnalyzeImagery from "@/components/field/AnalyzeImagery";
import { useTranslation } from "react-i18next";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";

export const Route = createFileRoute(
    "/dashboard/fields/$fieldId/insights/settings",
)({
    component: RouteComponent,
});

function RouteComponent() {
    const { t } = useTranslation("insights");
    const { useMutateRequest } = useRequest();
    const { userIsSentinel } = useAuth();
    const { plotEditingEnabled } = useFeatureFlag();
    const { fieldId } = useParams({
        from: "/dashboard/fields/$fieldId",
    });

    const { fieldQuery } = useFieldDashboard() ?? {};

    useEffect(() => {
        if (!fieldQuery?.data) return;
        const insights =
            fieldQuery.data.insights_active_manual == null
                ? "auto"
                : fieldQuery.data.insights_active_manual
                  ? "active"
                  : "inactive";
        const notifications =
            fieldQuery.data.notifications_active_manual === null
                ? "auto"
                : fieldQuery.data.notifications_active_manual
                  ? "active"
                  : "inactive";
        setNotificationsActive(notifications);
        setInsightsActive(insights);
        const fieldSensitivity =
            fieldQuery.data.insight_sensitivity == "low"
                ? 0
                : fieldQuery.data.insight_sensitivity == "high"
                  ? 1
                  : 0.5;
        setSensitivity(fieldSensitivity);
    }, [fieldQuery?.data]);

    const patchField = useMutateRequest({
        url: `fields/${fieldId}/`,
        method: "patch",
        successMessage: t("messages.updatedInsightsSettings"),
        errorMessage: t("messages.failedToUpdateSettings"),
    });

    const [insightsActive, setInsightsActive] = useState("auto");
    const [notificationsActive, setNotificationsActive] = useState("auto");
    const [senstivity, setSensitivity] = useState(0);

    const onInsightsChange = useCallback(
        (val: Key) => {
            setInsightsActive(val as string);
            const insights_active_manual =
                val === "auto" ? null : val === "active";
            patchField.mutate(
                {
                    data: { insights_active_manual },
                },
                {
                    onSuccess: () => {
                        fieldQuery?.refetch();
                    },
                },
            );
        },
        [fieldQuery, patchField],
    );

    const onNotificationsChange = useCallback(
        (val: Key) => {
            setNotificationsActive(val as string);
            const notifications_active_manual =
                val === "auto" ? null : val === "active";
            patchField.mutate(
                {
                    data: { notifications_active_manual },
                },
                {
                    onSuccess: () => {
                        fieldQuery?.refetch();
                    },
                },
            );
        },
        [fieldQuery, patchField],
    );

    const onSensitivityChange = useCallback(
        (val: number | number[]) => {
            setSensitivity(val as number);
            const insight_sensitivity =
                val === 0 ? "low" : val == 0.5 ? "moderate" : "high";
            patchField.mutate(
                {
                    data: { insight_sensitivity },
                },
                {
                    onSuccess: () => {
                        fieldQuery?.refetch();
                    },
                },
            );
        },
        [fieldQuery, patchField],
    );

    return (
        <div className="flex flex-col gap-4 p-2 mb-40">
            <div className="flex gap-2 items-center">
                <Button
                    size="sm"
                    color="primary"
                    isIconOnly
                    as={Link}
                    to="/dashboard/fields/$fieldId/insights"
                    search={{
                        // @ts-expect-error - Button -> Link
                        advisor: undefined,
                    }}
                    params={
                        {
                            fieldId,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } as any
                    }
                    aria-label={t("ariaLabels.backToInsights")}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </Button>
                <div className="text-xl font-semibold">
                    {t("insights")} {t("settings.title")}
                </div>
            </div>
            <div className="flex md:flex-row  flex-col justify-between">
                <div>
                    <div className="font-semibold">
                        {t("settings.insightsActive")}
                    </div>
                    <Tabs
                        selectedKey={insightsActive}
                        onSelectionChange={onInsightsChange}
                    >
                        <Tab key="auto" title={t("settings.auto")} />
                        <Tab key="active" title={t("settings.active")} />
                        <Tab key="inactive" title={t("settings.inactive")} />
                    </Tabs>
                </div>
                <div>
                    <div className="font-semibold">
                        {t("settings.notificationsActive")}
                    </div>
                    <Tabs
                        selectedKey={notificationsActive}
                        onSelectionChange={onNotificationsChange}
                    >
                        <Tab key="auto" title={t("settings.auto")} />
                        <Tab key="active" title={t("settings.active")} />
                        <Tab key="inactive" title={t("settings.inactive")} />
                    </Tabs>
                </div>
            </div>
            <div>
                <div className="font-semibold">
                    {t("settings.insightsSensitivity")}
                </div>
                <div className="px-2">
                    <Slider
                        aria-label={t("settings.insightsSensitivity")}
                        maxValue={1}
                        minValue={0}
                        className="px-2"
                        showSteps={true}
                        value={senstivity}
                        onChangeEnd={onSensitivityChange}
                        step={0.5}
                    />
                </div>
                <div className="flex justify-between text-sm">
                    <div>{t("settings.low")}</div>
                    <div>{t("settings.moderate")}</div>
                    <div>{t("settings.high")}</div>
                </div>
            </div>
            {(userIsSentinel || plotEditingEnabled) && (
                <>
                    <ActivePlots />
                    {userIsSentinel && <AnalyzeImagery />}
                </>
            )}
        </div>
    );
}
